// היומן — the calendar screen: its views, the booking form, the summary and what follows
// it. Owned entirely by this file, including its nav label, so a feature in any other area
// never has to open it (and this one never has to open theirs).
//
// The nav label lives under `calendar.nav` rather than in shared.nav ON PURPOSE.
// strings/shared.js is described in its own header as "the one file a feature in any area
// may still need to touch", and the whole point of the split was to stop features
// colliding there. A screen that can own its own label should.
//
// Pure data — no imports, ever. See he.js.
const calendar = {
  calendar: {
    nav: 'יומן',
    title: 'יומן',

    // THE CAPTIONS OVER THE THREE CONTROL GROUPS. They exist because the groups change
    // three different things and used to sit in one undifferentiated row — a caption is
    // the only part of the design that NAMES what a group does rather than leaving it to
    // be discovered by clicking. Kept to one word each so the bar stays a bar.
    groupRange: 'טווח',
    groupView: 'תצוגה',
    groupWho: 'של מי',

    // The three framings of one window of time.
    day: 'יום',
    week: 'שבוע',
    month: 'חודש',

    // How that window is drawn. Not a filter — the same meetings, two renderings.
    viewGrid: 'לוח',
    viewList: 'רשימה',

    today: 'היום',
    prev: 'הקודם',
    next: 'הבא',

    // The filter. Three options rather than two: the per-partner colours only earn their
    // keep in the combined view, so "הכל" is the default and each partner's name is its
    // own option.
    allPartners: 'הכל',
    onlyMine: 'שלי',

    newMeeting: 'פגישה חדשה',
    editMeeting: 'עריכת פגישה',

    // The two kinds. A meeting is with a client or between the partners, and the form
    // asks which before anything else — the rest of the fields depend on the answer.
    kind: 'סוג פגישה',
    kindClient: 'פגישה עם לקוח',
    kindInternal: 'פגישה פנימית',
    internalLabel: 'פגישה פנימית',
    // Someone who is neither a client nor a lead (077): the name typed freely.
    kindExternal: 'פגישה עם גורם חיצוני',
    externalName: 'עם מי',
    externalDetails: 'פרטים',
    externalDetailsPlaceholder: 'למשל: חברה, תפקיד, טלפון',
    externalBadge: 'חיצוני',
    metWith: (name) => `נפגשנו עם ${name}`,

    // Linking an external meeting to the client or lead the person became.
    makeLead: 'הפיכה לליד',
    linkClient: 'שיוך ללקוח או ליד',
    linkTitle: (name) => `שיוך הפגישות עם ${name}`,
    linkConfirm: 'שיוך',
    linkToWhom: 'לקוח או ליד',
    linkChain: 'הפגישה ופגישות ההמשך שלה',
    linkSameName: (name) => `פגישות נוספות עם ${name}`,
    linkAddContact: (name) => `הוספת ${name} כאיש קשר`,

    // Finding a meeting across every date.
    search: 'חיפוש בפגישות ובסיכומים',
    searchEmpty: 'לא נמצאו פגישות.',

    client: 'לקוח',
    job: 'עבודה',
    noJob: 'ללא שיוך לעבודה',
    owner: 'שייך ל',
    withPartner: 'עם',
    title_: 'נושא',
    titlePlaceholder: 'למשל: פגישת עדכון חודשית',
    noTitle: 'פגישה',

    date: 'תאריך',
    startTime: 'שעת התחלה',
    duration: 'משך',
    // Durations offered in the form, built from the half-hour grid.
    minutes: (n) => (n < 60
      ? `${n} דקות`
      : (n % 60 === 0
        ? (n === 60 ? 'שעה' : `${n / 60} שעות`)
        : `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')} שעות`)),

    // The slot picker. Working hours are 08:00–24:00 on the half hour, and a slot that is
    // not offerable says WHY — "תפוס" means try another time, "לא נשאר מספיק זמן" means
    // shorten the meeting. Two different problems with two different answers.
    pickTime: 'בחירת שעה',
    // The honest empty state. It replaced "בודק שעות פנויות..." being shown when there is
    // no date — which claimed to be working while it was in fact waiting for the user, and
    // so gave no reason to do anything about it.
    pickDateFirst: 'יש לבחור תאריך, ואז יוצגו כאן השעות הפנויות באותו יום.',
    pickOwnerFirst: 'יש לבחור ביומן של מי הפגישה, ואז יוצגו כאן השעות הפנויות.',
    slotTaken: 'תפוס',
    slotPastEnd: 'לא נשאר מספיק זמן ביום',
    noFreeSlots: 'אין שעות פנויות ביום הזה באורך שנבחר. אפשר לקצר את הפגישה או לבחור יום אחר.',
    loadingSlots: 'בודק שעות פנויות...',
    // Whose diary is being checked. It says so out loud because the answer changes when
    // the owner changes, and a partner needs to know which diary they are looking at.
    slotsFor: (name) => `שעות פנויות ביומן של ${name}`,
    slotsForBoth: 'שעות פנויות בשני היומנים',

    // Reassignment.
    reassign: 'העברה לשותף',
    reassignTo: (name) => `העברה ל${name}`,

    // The summary, recorded after the meeting.
    summaryTitle: 'סיכום פגישה',
    actualDuration: 'כמה זמן הפגישה נמשכה בפועל',
    summaryText: 'סיכום',
    summaryPlaceholder: 'מה סוכם, מה נדרש, ומי אחראי',
    summarySaved: 'הסיכום נשמר.',
    scheduled: 'מתוכננת',
    held: 'התקיימה',
    viewSummary: 'צפייה בסיכום',
    editSummary: 'עריכת הסיכום',

    // The three things that can follow a summary. Each is optional — closing the dialog
    // here is a complete action.
    whatNext: 'מה הלאה?',
    followUpMeeting: 'קביעת פגישת המשך',
    addTask: 'הוספת משימה',
    addLead: 'הוספת ליד',
    // The lead flow lives on its own screen, so this says where it is going before it
    // goes — a button that silently changes the page is a button nobody presses twice.
    addLeadHint: 'מעבר למסך הלידים',

    // Cancelling, and the other thing.
    cancelMeeting: 'ביטול הפגישה',
    cancelQuestion: 'לבטל את הפגישה?',
    cancelExplain: 'הפגישה תסומן כמבוטלת, והשעה שלה תתפנה.',
    cancelReason: 'סיבה',
    cancelReasonPlaceholder: 'למשל: הלקוח ביטל',
    cancelConfirm: 'ביטול הפגישה',
    cancelled: 'מבוטלת',
    reinstate: 'החזרת הפגישה',
    // Deleting is the OTHER act, and the dialog says which is which so the two are not
    // confused at the moment of choosing.
    deleteMeeting: 'מחיקת הפגישה',
    deleteExplain: 'לפגישה שבוטלה עדיף לסמן ביטול.',

    // Column headings for list mode.
    colWhen: 'מתי',
    colTime: 'שעה',
    colWith: 'עם',
    colOwner: 'של מי',
    colStatus: 'סטטוס',

    // Day names, Sunday first — the week as it is worked here.
    weekdays: ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'],
    weekdaysShort: ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'],

    // The line the CLIENT, JOB and LEAD delete dialogs add to their "what goes with it"
    // list. It lives here rather than in shared.js beside deleteTasks / deleteInvoices,
    // so the calendar owns every word it put on a screen and shared.js stays the one file
    // no feature has to open. The dialogs read it as he.calendar.deleteMeetings.
    //
    // It names the SUMMARIES too, because that is the part that cannot be reconstructed:
    // a re-bookable hour is not a loss, a written record of what was agreed is.
    deleteMeetings: 'פגישות — כולל הסיכומים שנכתבו להן',

    // ── THE CLIENT CARD'S MEETINGS TAB ──────────────────────────────────────────────
    // The reverse view of the calendar: one client's whole history in one place. The
    // section headings say where each group sits in time, because the order alone does not
    // — a list that simply ran from soonest to oldest would put next week above last week
    // with nothing marking the boundary between what is coming and what already happened.
    clientUpcoming: 'פגישות קרובות',
    clientPast: 'פגישות שהתקיימו',
    clientCancelled: 'פגישות שבוטלו',
    clientEmpty: 'עדיין לא נקבעו פגישות עם הלקוח הזה.',
    // The scheduled length, and beside it the length it actually ran. Two different facts
    // (see migration 055), so the second never replaces the first.
    clientActual: (label) => `נמשכה בפועל ${label}`,
    clientNoSummary: 'לא נכתב סיכום לפגישה הזו.',

    empty: 'אין פגישות בטווח הזה.',
    emptyDay: 'אין פגישות ביום הזה.',
    emptyFiltered: 'אין פגישות שתואמות לסינון.',
    more: (n) => `עוד ${n}`,
  },
};

export default calendar;
